import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import compact from 'lodash/compact';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import startsWith from 'lodash/startsWith';

import { CompactPeopleSecondaryMenuLinksConfig } from './useCompactPeopleSecondaryMenu.types';
import { IconsEnum } from '../../../../../../../assets/icons/types';
import { PeopleSecondaryMenuItems } from '../../CompactPeopleSecondaryMenu.types';

import { useCurrentUser } from '../../../../../../../auth/hooks/useAuth';

import { TeamPath } from '../../../../../../teams/TeamPath';
import { UserPath } from '../../../../../../users/UserPath';

import { getPeopleActiveMenu } from '../../utils/getPeopleActiveMenu';

import { TeamsPermissions } from '../../../../../../teams/teamsConstants';
import { UsersPermissions } from '../../../../../../users/usersConstants';

import { teamsKeys, menuKeys } from '../../../../../../../locales/keys';

function useCompactPeopleSecondaryMenu() {
  const { pathname } = useRouter();

  const currentUser = useCurrentUser();

  const peopleMenuLinks = useCallback(
    (permissions: {
      companies: string[];
      performers: string[];
      clients: string[];
    }) =>
      compact([
        currentUser.hasPermissions(
          TeamsPermissions.READ_PEOPLE_MENU_MY_COMPANIES_LINK
        )
          ? {
              name: PeopleSecondaryMenuItems.COMPANIES,
              href: TeamPath.companiesCompactMyCompanies(),
              i18nText: teamsKeys.companies,
              activeTest: (pathname: string) =>
                startsWith(pathname, TeamPath.companiesCompactMyCompanies()) &&
                !startsWith(pathname, TeamPath.companiesGrid()),
              permissions: permissions.companies
            }
          : null,
        currentUser.hasPermissions(
          TeamsPermissions.READ_PEOPLE_MENU_COMPANIES_GRID_LINK
        )
          ? {
              name: PeopleSecondaryMenuItems.COMPANIES_GRID,
              href: TeamPath.companiesGrid(),
              i18nText: teamsKeys.companies,
              activeTest: (pathname: string) =>
                startsWith(pathname, TeamPath.companiesGrid()) &&
                !startsWith(pathname, TeamPath.companiesGridBlocked()),
              permissions: [
                TeamsPermissions.READ_PEOPLE_MENU_COMPANIES_GRID_LINK
              ]
            }
          : null,
        currentUser.hasPermissions(
          TeamsPermissions.READ_PEOPLE_MENU_BLOCKED_COMPANIES_GRID_LINK
        )
          ? {
              name: PeopleSecondaryMenuItems.BLOCKED_COMPANIES_GRID,
              href: TeamPath.companiesGridBlocked(),
              i18nText: teamsKeys.blockedCompanies,
              activeTest: (pathname: string) =>
                startsWith(pathname, TeamPath.companiesGridBlocked()),
              permissions: [
                TeamsPermissions.READ_PEOPLE_MENU_BLOCKED_COMPANIES_GRID_LINK
              ]
            }
          : null,
        currentUser.hasPermissions(
          TeamsPermissions.READ_PEOPLE_MENU_PERFORMERS_LINK
        )
          ? {
              name: PeopleSecondaryMenuItems.PERFORMERS,
              href: TeamPath.performersCompact(),
              i18nText: teamsKeys.performers,
              activeTest: (pathname: string) =>
                startsWith(pathname, TeamPath.performersCompact()),
              permissions: permissions.performers
            }
          : null,
        currentUser.hasPermissions(
          TeamsPermissions.READ_PEOPLE_MENU_CLIENTS_LINK
        )
          ? {
              name: PeopleSecondaryMenuItems.CLIENTS,
              href: UserPath.clientsCompact(),
              i18nText: teamsKeys.clients,
              activeTest: (pathname: string) =>
                startsWith(pathname, UserPath.clientsCompact()),
              permissions: permissions.performers
            }
          : null
      ]),
    [currentUser]
  );

  const config = useMemo<CompactPeopleSecondaryMenuLinksConfig>(
    () => ({
      companies: [
        {
          action: TeamsPermissions.READ_COMPANIES_COMPACT_MENU_INDEX_LINK,
          active: pathname === TeamPath.companiesCompact(),
          href: TeamPath.companiesCompact(),
          i18nText: teamsKeys.allCompanies,
          icon: IconsEnum.VIEW_LIST
        },
        {
          action:
            TeamsPermissions.READ_COMPANIES_COMPACT_MENU_MY_COMPANIES_LINK,
          active: pathname === TeamPath.companiesCompactMyCompanies(),
          href: TeamPath.companiesCompactMyCompanies(),
          i18nText: teamsKeys.myCompanies,
          icon: IconsEnum.VIEW_LIST_USER_SOLID
        },
        {
          action: TeamsPermissions.READ_COMPANIES_COMPACT_MENU_BLOCKED_LINK,
          active: pathname === TeamPath.companiesCompactBlocked(),
          href: TeamPath.companiesCompactBlocked(),
          i18nText: teamsKeys.blockedCompanies,
          icon: IconsEnum.VIEW_LIST_NO_SYMBOL_SOLID
        },
        {
          action: TeamsPermissions.READ_COMPANIES_COMPACT_MENU_UNASSIGNED_LINK,
          active: pathname === TeamPath.companiesCompactUnassigned(),
          href: TeamPath.companiesCompactUnassigned(),
          i18nText: menuKeys.links.unassigned,
          icon: IconsEnum.FIRE_SOLID
        }
      ],
      performers: [
        {
          action: TeamsPermissions.READ_PERFORMERS_COMPACT_MENU_INDEX_LINK,
          active: pathname === TeamPath.performersCompact(),
          href: TeamPath.performersCompact(),
          i18nText: menuKeys.links.all,
          icon: IconsEnum.VIEW_LIST_SOLID
        },
        {
          action:
            TeamsPermissions.READ_PERFORMERS_COMPACT_MENU_MY_PERFORMERS_LINK,
          active: pathname === TeamPath.performersCompactMyPerformers(),
          href: TeamPath.performersCompactMyPerformers(),
          i18nText: menuKeys.links.my,
          icon: IconsEnum.VIEW_LIST_USER_SOLID
        },
        {
          action: TeamsPermissions.READ_PERFORMERS_COMPACT_MENU_FAVORITE_LINK,
          active: pathname === TeamPath.performersCompactFavorite(),
          href: TeamPath.performersCompactFavorite(),
          i18nText: menuKeys.links.favorite,
          icon: IconsEnum.FILLED_STAR
        },
        {
          action: TeamsPermissions.READ_PERFORMERS_COMPACT_MENU_UNASSIGNED_LINK,
          active: pathname === TeamPath.performersCompactUnassigned(),
          href: TeamPath.performersCompactUnassigned(),
          i18nText: menuKeys.links.unassigned,
          icon: IconsEnum.BURNING
        },
        {
          action: TeamsPermissions.READ_PERFORMERS_COMPACT_MENU_TAT_LINK,
          active: pathname === TeamPath.performersCompactTat(),
          href: TeamPath.performersCompactTat(),
          i18nText: teamsKeys.tat,
          icon: IconsEnum.COLLECTION_SOLID
        },
        {
          action: TeamsPermissions.READ_PERFORMERS_COMPACT_MENU_BLOCKED_LINK,
          active: pathname === TeamPath.performersCompactBlocked(),
          href: TeamPath.performersCompactBlocked(),
          i18nText: menuKeys.links.blocked,
          icon: IconsEnum.RECEIPT_REFUND
        }
      ],
      clients: [
        {
          action: UsersPermissions.READ_CLIENTS_COMPACT_MENU_INDEX_LINK,
          active: pathname === UserPath.clientsCompact(),
          href: UserPath.clientsCompact(),
          i18nText: menuKeys.links.all,
          icon: IconsEnum.VIEW_LIST_SOLID
        },
        {
          action: UsersPermissions.READ_CLIENTS_COMPACT_MENU_FAVORITE_LINK,
          active: pathname === UserPath.clientsCompactFavorite(),
          href: UserPath.clientsCompactFavorite(),
          i18nText: menuKeys.links.favorite,
          icon: IconsEnum.FILLED_STAR
        }
      ]
    }),
    [pathname]
  );

  const activeMenu = getPeopleActiveMenu(pathname);

  return {
    activeMenu,
    config,
    peopleMenuLinks,
    permissions: mapValues(config, (item) => map(item, 'action'))
  };
}

export default useCompactPeopleSecondaryMenu;
